/**
 * Admin Master — Gestão Pública (Leads institucionais).
 *
 * v0.99.11 · Fev/2026 — Redesign no padrão dark do Admin Master
 * (mesma linguagem visual de AdminDashboard). Leads capturados via
 * /gov/leads. Só super_admin acessa. Backend valida.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Buildings, FunnelSimple } from "@phosphor-icons/react";

const STATUS_LABELS = {
  novo: "Novo",
  contactado: "Contactado",
  reuniao_agendada: "Reunião agendada",
  proposta_enviada: "Proposta enviada",
  fechado: "Fechado",
  perdido: "Perdido",
};

// Badges: fundo semi-transparente + texto brilhante — leitura ok no dark.
const STATUS_BADGE = {
  novo:             "bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/25",
  contactado:       "bg-sky-500/15 text-sky-300 ring-1 ring-inset ring-sky-500/25",
  reuniao_agendada: "bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/25",
  proposta_enviada: "bg-violet-500/15 text-violet-300 ring-1 ring-inset ring-violet-500/25",
  fechado:          "bg-white/10 text-white ring-1 ring-inset ring-white/20",
  perdido:          "bg-rose-500/15 text-rose-300 ring-1 ring-inset ring-rose-500/25",
};

// Accents pro valor grande do StatCard (por status).
const STATUS_ACCENT = {
  novo: "text-emerald-300",
  contactado: "text-sky-300",
  reuniao_agendada: "text-amber-300",
  proposta_enviada: "text-violet-300",
  fechado: "text-white",
  perdido: "text-rose-300",
};

const ORG_LABELS = {
  gov_municipal:   "Prefeitura Municipal",
  gov_secretariat: "Secretaria Municipal",
  gov_chamber:     "Câmara Municipal",
  gov_state:       "Órgão Estadual",
};

export default function AdminGov() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/admin/gov/leads" + (filter ? `?status_filter=${filter}` : "")),
        api.get("/admin/gov/stats"),
      ]);
      setLeads(l.data?.leads || []);
      setStats(s.data || { total: 0, by_status: {} });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar leads GOV");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/gov/leads/${id}/status`, { status });
      toast.success("Status atualizado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1400px] mx-auto" data-testid="admin-gov-page">
      {/* Header — mesmo padrão do AdminDashboard */}
      <header className="mb-7">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
          Painel institucional
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1 flex items-center gap-2.5">
          <Buildings size={26} weight="duotone" className="text-emerald-300" />
          Gestão Pública
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">
          Leads institucionais capturados via <code className="bg-white/[0.06] border border-white/10 px-1.5 py-0.5 rounded text-slate-300 text-[12px]">/gov</code>.
          Contratação por processo administrativo (sem Stripe).
        </p>
      </header>

      {/* Stats — grid dark alinhado com Mini/HeroKpi */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="admin-gov-stats">
        <StatCard label="Total" value={stats.total} accent="text-white" hero />
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <StatCard
            key={k}
            label={v}
            value={stats.by_status?.[k] || 0}
            accent={STATUS_ACCENT[k]}
            badge={STATUS_BADGE[k]}
          />
        ))}
      </div>

      {/* Filter — controle dark */}
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">
          <FunnelSimple size={12} weight="bold" />
          Filtrar
        </span>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="admin-gov-filter"
          className="px-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-md text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:ring-1 focus:ring-white/30 transition-colors"
        >
          <option value="" className="bg-[#0A0A0B]">Todos</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k} className="bg-[#0A0A0B]">{v}</option>
          ))}
        </select>
      </div>

      {/* Tabela de leads — card dark */}
      <div className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Carregando…</div>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">Nenhum lead encontrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-gov-leads-table">
              <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold border-b border-white/10">
                <tr>
                  <th className="px-4 py-3 text-left">Órgão</th>
                  <th className="px-4 py-3 text-left">Contato</th>
                  <th className="px-4 py-3 text-left">Local</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {leads.map((l) => (
                  <tr key={l.id} className="hover:bg-white/[0.03] transition-colors" data-testid={`admin-gov-lead-${l.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-white">{l.org_name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{ORG_LABELS[l.org_type] || l.org_type}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-slate-200">{l.contact_name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{l.contact_email} · {l.contact_phone}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-300">{l.city}/{l.state}</td>
                    <td className="px-4 py-3 align-top">
                      <span className="text-[10.5px] uppercase tracking-[0.1em] px-2 py-0.5 rounded bg-white/[0.06] border border-white/10 text-slate-300">
                        {l.request_type === "demo" ? "Demo" : l.request_type === "proposta" ? "Proposta" : "Apresentação"}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`text-[10.5px] px-2 py-0.5 rounded ${STATUS_BADGE[l.status] || "bg-white/[0.06] text-slate-300 ring-1 ring-inset ring-white/10"}`}>
                        {STATUS_LABELS[l.status] || l.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <select
                        onChange={(e) => setStatus(l.id, e.target.value)}
                        defaultValue=""
                        data-testid={`admin-gov-status-${l.id}`}
                        className="text-[11px] bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:ring-1 focus:ring-white/30 transition-colors"
                      >
                        <option value="" disabled className="bg-[#0A0A0B]">Atualizar…</option>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k} className="bg-[#0A0A0B]">{v}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, badge, hero }) {
  return (
    <div
      className={`${hero ? "bg-white/[0.03]" : "bg-white/[0.02]"} border border-white/10 rounded-lg p-4 hover:bg-white/[0.05] transition-colors`}
      data-testid={`admin-gov-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {badge ? (
        <span className={`inline-block text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded ${badge}`}>
          {label}
        </span>
      ) : (
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">{label}</div>
      )}
      <div className={`font-heading text-2xl font-semibold mt-2 tabular-nums tracking-tight ${accent || "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
